import React, { useState, useEffect, useCallback } from 'react';
import CommonModal from '../Components/CommonModal';
import { ConfirmModal as CommonConfirmModal } from '../Components/ConfirmModal';
import CommonTable from '../Components/CommonTable';
import CommonButton from '../Components/CommonButton';
import {
    getPlatformUsers, addPlatformUser, unassignRoleFromUser,
    getOrgMembers, addOrgMember, updateOrgMember, deleteOrgMember,
    getReportEmailRecipients, addReportEmailRecipient, updateReportEmailRecipient, deleteReportEmailRecipient,
} from '../services';
import { getUserFlags } from '../helper';
import { lightTheme } from '../Constants/Colors';
import { subHeading2, body, bodySmall, caption, fontFamily, fontWeight } from '../Constants/Fonts';
import useSsoDiscovery from '../Auth/ssoAuth/useSsoDiscovery';
import PageHeading from '../Components/PageHeading';

// ─── Role display config ─────────────────────────────────────────────────────
const ROLE_META = {
    super_admin: { label: 'Super Admin', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
    sub_admin: { label: 'Sub Admin', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
    tam: { label: 'TAM', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
    mssp: { label: 'MSSP', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
    org_admin: { label: 'Org Admin', color: '#16a34a', bg: 'rgba(22,163,74,0.15)' },
    org_user: { label: 'Org User', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
    vendor: { label: 'Vendor', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

const PLATFORM_ROLE_OPTIONS = [
    { value: 'sub_admin', label: 'Sub Admin' },
    { value: 'tam', label: 'TAM (Technical Account Manager)' },
    { value: 'mssp', label: 'MSSP (Managed Security Provider)' },
];

const ORG_ROLE_OPTIONS = [
    { value: 'org_admin', label: 'Organization Admin' },
    { value: 'org_user', label: 'Organization User' },
    { value: 'brand_risk_analyst', label: 'Brand Protection' },
];

const REPORT_TYPE_OPTIONS = [
    { value: 'cxo_summary', label: 'CXO Summary' },
    { value: 'brand_risk', label: 'Brand Risk' },
    { value: 'detailed_report', label: 'Detailed Security Report' },
    { value: 'dark_web', label: 'Dark Web' },
];

// ─── Token-driven styles (colours from Colors.js, type from Fonts.js) ────────
const S = {
    container: { display: 'flex', flexDirection: 'column', gap: 28, padding: '4px 0', fontFamily },
    section: { background: lightTheme.surface, border: `1px solid ${lightTheme.border}`, borderRadius: 10, padding: 24 },
    sectionHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, gap: 12, flexWrap: 'wrap' },
    title: { ...subHeading2({ color: lightTheme.textPrimary }), margin: '0 0 4px' },
    sub: { ...bodySmall({ color: lightTheme.textSecondary }), margin: 0 },
    inlineError: { ...bodySmall({ color: lightTheme.danger }), marginTop: 10 },
    loading: { ...bodySmall({ color: lightTheme.textSecondary }), textAlign: 'center', padding: '24px 0' },
    actionsCell: { display: 'flex', gap: 6 },
    youTag: {
        display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 10,
        ...caption({ fontWeight: fontWeight.semibold, color: lightTheme.primary }),
        background: lightTheme.primarySoft, border: `1px solid ${lightTheme.primary}33`, verticalAlign: 'middle',
    },
    dim: { ...bodySmall({ color: lightTheme.textMuted }) },
};

// ─── Report-recipient response helpers ───────────────────────────────────────
function extractRecipientList(res) {
    const s = res?.statusCode;
    if (Array.isArray(s)) return s;
    const candidates = [
        s?.response_data?.data?.recipients,
        s?.data?.recipients,
        s?.response_data?.data,
        s?.response_data?.results,
        s?.data,
        s?.results,
    ];
    return candidates.find(Array.isArray) || [];
}
function recipientOk(res) {
    if (res?.errors) return false;
    const s = res?.statusCode;
    if (s && (s.status === 0 || s.status_code === 0)) return false;
    return true;
}

// ─── Badges ──────────────────────────────────────────────────────────────────
function RoleBadge({ slug }) {
    const meta = ROLE_META[slug] || { label: slug || '—', color: lightTheme.textSecondary, bg: 'rgba(148,163,184,0.15)' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 12, letterSpacing: '0.4px',
            ...caption({ fontWeight: fontWeight.semibold, color: meta.color }),
            background: meta.bg, border: `1px solid ${meta.color}33`,
        }}>
            {meta.label}
        </span>
    );
}

function StatusBadge({ status }) {
    const active = String(status || '').toLowerCase() === 'active';
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 12,
            ...caption({ fontWeight: fontWeight.semibold, color: active ? lightTheme.success : lightTheme.danger }),
            background: active ? lightTheme.successSoft : lightTheme.dangerSoft,
            border: `1px solid ${active ? lightTheme.success : lightTheme.danger}33`,
        }}>
            {active ? 'Active' : 'Inactive'}
        </span>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section A — Platform Users  (super_admin only)
// ═══════════════════════════════════════════════════════════════════════════
function PlatformUsersSection() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [confirm, setConfirm] = useState(null); // { user, assignmentId }
    const [busy, setBusy] = useState(false);
    const [addErr, setAddErr] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await getPlatformUsers();
            const d = res?.statusCode?.data || res?.statusCode?.response_data?.data || [];
            setUsers(Array.isArray(d) ? d : []);
        } catch { setError('Failed to load platform users.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async (data) => {
        setBusy(true); setAddErr('');
        try {
            const res = await addPlatformUser(data);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) { setShowAdd(false); load(); }
            else setAddErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to create user.');
        } catch { setAddErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const handleUnassign = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            await unassignRoleFromUser(confirm.assignmentId);
            setConfirm(null); load();
        } catch { setConfirm(null); }
        finally { setBusy(false); }
    };

    const rows = users.map(u => ({
        data: [
            u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—',
            u.emailId,
            u.role_info ? <RoleBadge slug={u.role_info.slug} /> : <span style={S.dim}>No role</span>,
            <StatusBadge status={u.status || 'Active'} />,
        ],
        _record: u,
    }));

    return (
        <div style={S.section}>
            <div style={S.sectionHeader}>
                <div>
                    <h3 style={S.title}>Platform Users</h3>
                    <p style={S.sub}>Sub Admins, TAMs, and MSSPs managing the platform.</p>
                </div>
                <CommonButton text="+ Add Platform User" onClick={() => { setShowAdd(true); setAddErr(''); }} />
            </div>

            {error && <p style={S.inlineError}>{error}</p>}

            {loading
                ? <p style={S.loading}>Loading…</p>
                : (
                    <CommonTable
                        headers={['Name', 'Email', 'Role', 'Status', 'Actions']}
                        rows={rows}
                        actions={(row) => row._record.role_info?.assignment_id ? (
                            <CommonButton
                                text="Unassign Role"
                                variant="danger"
                                onClick={() => setConfirm({ user: row._record, assignmentId: row._record.role_info.assignment_id })}
                            />
                        ) : null}
                        noPadding
                        noScroll
                    />
                )}

            {/* Add platform user */}
            <CommonModal
                show={showAdd}
                onHide={() => setShowAdd(false)}
                mid
                title="Add Platform User"
                subtitle="Create a platform-level user and assign their role."
                fields={[
                    { name: 'first_name', label: 'First Name', half: true, required: true },
                    { name: 'last_name', label: 'Last Name', half: true, required: true },
                    { name: 'emailId', label: 'Email', type: 'email', required: true },
                    { name: 'role_slug', label: 'Role', type: 'select', options: PLATFORM_ROLE_OPTIONS },
                    { name: 'password', label: 'Password', type: 'password', half: true, required: true },
                    { name: 'confirm_password', label: 'Confirm Password', type: 'password', half: true, required: true, validate: (v, all) => v === all.password || 'Passwords do not match' },
                ]}
                defaultValues={{ first_name: '', last_name: '', emailId: '', role_slug: 'sub_admin', password: '', confirm_password: '' }}
                onSubmit={handleAdd}
                error={addErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setShowAdd(false), disabled: busy },
                    { text: busy ? 'Creating…' : 'Create User', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Creating…' },
                ]}
            />

            {/* Unassign role confirm */}
            <CommonConfirmModal
                themeColor={false}
                state={confirm ? {
                    title: 'Unassign Role',
                    message: `Remove the ${confirm?.user?.role_info?.role_name} role from ${confirm?.user?.full_name || confirm?.user?.emailId}? The account remains active with no platform role.`,
                    confirmLabel: busy ? 'Removing…' : 'Confirm',
                    cancelLabel: 'Cancel',
                    danger: true,
                } : null}
                onClose={(ok) => { if (ok) handleUnassign(); else setConfirm(null); }}
            />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section B — Org Members
// ═══════════════════════════════════════════════════════════════════════════
function OrgMembersSection({ organizationId }) {
    const flags = getUserFlags();
    const currentEmail = (localStorage.getItem('email') || '').toLowerCase();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [delItem, setDelItem] = useState(null);
    const [busy, setBusy] = useState(false);
    const [addErr, setAddErr] = useState('');
    const [editErr, setEditErr] = useState('');

    // Report email recipient — add
    const [showRecipient, setShowRecipient] = useState(false);
    const [recipientErr, setRecipientErr] = useState('');

    // Report email recipient — list / edit / delete
    const [showRecipientList, setShowRecipientList] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsError, setRecipientsError] = useState('');
    const [editRecipient, setEditRecipient] = useState(null);
    const [editRecipientErr, setEditRecipientErr] = useState('');
    const [delRecipient, setDelRecipient] = useState(null);
    const [recipientBusy, setRecipientBusy] = useState(false);

    // Does this org federate via SSO? If so, invited members sign in through the
    // identity provider, so the Invite Member modal drops the password field.
    const userName = localStorage.getItem('email');
    const { sso } = useSsoDiscovery(userName);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await getOrgMembers(organizationId);
            const d = res?.statusCode?.data || res?.statusCode?.response_data?.data || [];
            setMembers(Array.isArray(d) ? d : []);
        } catch { setError('Failed to load organization members.'); }
        finally { setLoading(false); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async (data) => {
        setBusy(true); setAddErr('');
        try {
            // Tell the backend whether this org federates via SSO — the invited
            // member is provisioned for IdP sign-in (no password) when true.
            const res = await addOrgMember({ ...data, is_sso: Boolean(sso.ssoEnabled) }, organizationId);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) { setShowAdd(false); load(); }
            else setAddErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to add member.');
        } catch { setAddErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const handleEdit = async (data) => {
        if (!editItem) return;
        setBusy(true); setEditErr('');
        try {
            const res = await updateOrgMember(editItem.id, data, organizationId);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) { setEditItem(null); load(); }
            else setEditErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to update member.');
        } catch { setEditErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const handleDelete = async () => {
        if (!delItem) return;
        setBusy(true);
        try { await deleteOrgMember(delItem.id, organizationId); setDelItem(null); load(); }
        catch { setDelItem(null); }
        finally { setBusy(false); }
    };

    const loadRecipients = useCallback(async () => {
        setRecipientsLoading(true); setRecipientsError('');
        try {
            const res = await getReportEmailRecipients();
            setRecipients(extractRecipientList(res));
        } catch { setRecipientsError('Failed to load recipients.'); }
        finally { setRecipientsLoading(false); }
    }, []);

    const openRecipientList = () => { setShowRecipientList(true); loadRecipients(); };

    const handleAddRecipient = async (data) => {
        setBusy(true); setRecipientErr('');
        try {
            const payload = {
                email: (data.email || '').trim(),
                report_types: data.report_types || [],
                extra_data: { name: (data.name || '').trim(), designation: (data.designation || '').trim() },
                is_active: data.is_active,
            };
            const res = await addReportEmailRecipient(payload);
            if (recipientOk(res)) {
                setShowRecipient(false);
                if (showRecipientList) loadRecipients();
            } else {
                setRecipientErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to add recipient.');
            }
        } catch (err) {
            setRecipientErr(err?.response?.data?.error?.message || 'An unexpected error occurred.');
        }
        finally { setBusy(false); }
    };

    const handleEditRecipient = async (data) => {
        if (!editRecipient) return;
        setRecipientBusy(true); setEditRecipientErr('');
        try {
            const payload = {
                email: (data.email || '').trim(),
                report_types: data.report_types || [],
                extra_data: { name: (data.name || '').trim(), designation: (data.designation || '').trim() },
                is_active: data.is_active,
            };
            const res = await updateReportEmailRecipient(editRecipient.id, payload);
            if (recipientOk(res)) { setEditRecipient(null); loadRecipients(); }
            else setEditRecipientErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to update recipient.');
        } catch (err) {
            setEditRecipientErr(err?.response?.data?.error?.message || 'An unexpected error occurred.');
        }
        finally { setRecipientBusy(false); }
    };

    const handleDeleteRecipient = async () => {
        if (!delRecipient) return;
        setRecipientBusy(true);
        try { await deleteReportEmailRecipient(delRecipient.id); setDelRecipient(null); loadRecipients(); }
        catch { setDelRecipient(null); }
        finally { setRecipientBusy(false); }
    };

    const isSelf = (m) => (m.emailId || '').toLowerCase() === currentEmail;
    const getRoleSlug = (m) => m.org_role || m.org_role_info?.slug || '';
    // Active flag from the API (default active if the field is absent).
    const memberActive = (m) => m?.is_active ?? (String(m?.status || 'active').toLowerCase() !== 'inactive');
    const canManage = flags.is_platform_admin || flags.is_org_admin || flags.role_slug === 'mssp' || flags.role_slug === 'tam';

    const memberRows = members.map(m => ({
        data: [
            <>{`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}{isSelf(m) && <span style={S.youTag}>You</span>}</>,
            m.emailId,
            <RoleBadge slug={getRoleSlug(m)} />,
        ],
        _record: m,
    }));

    return (
        <div style={S.section}>
            <div style={S.sectionHeader}>
                <PageHeading title="Organization Members" subtitle="Admins and users within this organization." />
                {canManage && (
                    <div style={{ display: 'flex', gap: 10 }}>
                        <CommonButton text="Recipient List" onClick={openRecipientList} />
                        <CommonButton text="+ Report Recipient" onClick={() => { setShowRecipient(true); setRecipientErr(''); }} />
                        <CommonButton text="+ Invite Member" onClick={() => { setShowAdd(true); setAddErr(''); }} />
                    </div>
                )}
            </div>

            {error && <p style={S.inlineError}>{error}</p>}

            {loading
                ? <p style={S.loading}>Loading…</p>
                : (
                    <CommonTable
                        headers={canManage ? ['Name', 'Email', 'Role', 'Actions'] : ['Name', 'Email', 'Role']}
                        rows={memberRows}
                        actions={canManage ? (row) => (!isSelf(row._record) ? (
                            <div style={S.actionsCell}>
                                <CommonButton text="Edit" onClick={() => { setEditItem(row._record); setEditErr(''); }} />
                                <CommonButton
                                    text={memberActive(row._record) ? 'Deactivate' : 'Activate'}
                                    variant={memberActive(row._record) ? 'error' : 'success'}
                                    onClick={() => setDelItem(row._record)}
                                />
                            </div>
                        ) : null) : undefined}
                        noPadding
                        noScroll
                    />
                )}

            {/* Invite member */}
            <CommonModal
                show={showAdd}
                onHide={() => setShowAdd(false)}
                mid
                title="Invite Member"
                subtitle="Add a member to this organization."
                fields={[
                    { name: 'first_name', label: 'First Name', half: true, required: true },
                    { name: 'last_name', label: 'Last Name', half: true, required: true },
                    { name: 'emailId', label: 'Email', type: 'email', required: true },
                    { name: 'role_slug', label: 'Role', type: 'select', options: ORG_ROLE_OPTIONS },
                    // When the org federates via SSO the member signs in through the
                    // identity provider, so there's no temporary password to set.
                    ...(sso.ssoEnabled ? [] : [{ name: 'password', label: 'Temporary Password', type: 'password', required: true }]),
                ]}
                defaultValues={{ first_name: '', last_name: '', emailId: '', role_slug: 'org_user', password: '' }}
                onSubmit={handleAdd}
                error={addErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setShowAdd(false), disabled: busy },
                    { text: busy ? 'Inviting…' : 'Invite', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Inviting…' },
                ]}
            />

            {/* Edit member */}
            <CommonModal
                show={!!editItem}
                onHide={() => setEditItem(null)}
                mid
                title={`Edit — ${editItem ? (`${editItem.first_name || ''} ${editItem.last_name || ''}`.trim() || editItem.emailId) : ''}`}
                fields={[
                    { name: 'first_name', label: 'First Name', half: true },
                    { name: 'last_name', label: 'Last Name', half: true },
                    { name: 'role_slug', label: 'Role', type: 'select', options: ORG_ROLE_OPTIONS },
                ]}
                defaultValues={{
                    first_name: editItem?.first_name || '',
                    last_name: editItem?.last_name || '',
                    role_slug: editItem ? getRoleSlug(editItem) || 'org_user' : 'org_user',
                }}
                onSubmit={handleEdit}
                error={editErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setEditItem(null), disabled: busy },
                    { text: busy ? 'Saving…' : 'Save Changes', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' },
                ]}
            />

            {/* Activate / deactivate member confirm */}
            <CommonConfirmModal
                themeColor={false}
                state={delItem ? (() => {
                    const active = memberActive(delItem);
                    const name = (`${delItem.first_name || ''} ${delItem.last_name || ''}`.trim() || delItem.emailId);
                    return {
                        title: active ? 'Deactivate Member' : 'Activate Member',
                        message: active
                            ? `Deactivate ${name}? Their access will be suspended until reactivated.`
                            : `Activate ${name}? Their access will be restored.`,
                        confirmLabel: busy ? 'Updating…' : (active ? 'Deactivate' : 'Activate'),
                        cancelLabel: 'Cancel',
                        danger: active,
                    };
                })() : null}
                onClose={(ok) => { if (ok) handleDelete(); else setDelItem(null); }}
            />

            {/* Add report recipient */}
            <CommonModal
                show={showRecipient}
                onHide={() => setShowRecipient(false)}
                mid
                title="Add Report Recipient"
                subtitle="They'll receive scheduled reports by email."
                fields={[
                    { name: 'email', label: 'Email', type: 'email', placeholder: 'name@company.com', required: true },
                    { name: 'name', label: 'Name', type: 'text', half: true, required: true },
                    { name: 'designation', label: 'Designation', type: 'text', half: true, placeholder: 'e.g. Developer' },
                    { name: 'report_types', label: 'Report Types', type: 'multiselect', half: true, placeholder: 'Select report types', options: REPORT_TYPE_OPTIONS },
                    { name: 'is_active', label: 'Active', type: 'checkbox', half: true },
                ]}
                defaultValues={{ email: '', name: '', designation: '', report_types: [], is_active: true }}
                onSubmit={handleAddRecipient}
                error={recipientErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setShowRecipient(false), disabled: busy },
                    { text: 'Add Recipient', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' },
                ]}
            />

            {/* Report recipient list */}
            <CommonModal
                show={showRecipientList}
                onHide={() => setShowRecipientList(false)}
                title="Report Recipients"
                subtitle="People who receive scheduled reports by email."
                size="xl"
                buttons={[{ text: 'Close', onClick: () => setShowRecipientList(false) }]}
            >
                {recipientsError ? (
                    <p className="cmn-modal-error">{recipientsError}</p>
                ) : recipientsLoading ? (
                    <p style={S.loading}>Loading…</p>
                ) : (
                    <CommonTable
                        headers={['Email', 'Name', 'Designation', 'Report Types', 'Status', 'Actions']}
                        rows={recipients.map(r => ({
                            data: [
                                r.email || '—',
                                r.extra_data?.name || '—',
                                r.extra_data?.designation || '—',
                                (r.report_types && r.report_types.length) ? r.report_types.join(', ') : 'All',
                                <StatusBadge status={r.is_active ? 'active' : 'inactive'} />,
                            ],
                            _record: r,
                        }))}
                        actions={(row) => (
                            <div style={S.actionsCell}>
                                <CommonButton text="Edit" onClick={() => { setEditRecipient(row._record); setEditRecipientErr(''); }} />
                                <CommonButton text="Remove" variant='error' onClick={() => setDelRecipient(row._record)} />
                            </div>
                        )}
                        noPadding
                        noScroll
                    />
                )}
            </CommonModal>

            {/* Edit report recipient */}
            <CommonModal
                show={!!editRecipient}
                onHide={() => setEditRecipient(null)}
                mid
                title="Edit Report Recipient"
                subtitle="Update this recipient's details."
                fields={[
                    { name: 'email', label: 'Email', type: 'email', placeholder: 'name@company.com', required: true },
                    { name: 'name', label: 'Name', type: 'text', half: true, required: true },
                    { name: 'designation', label: 'Designation', type: 'text', half: true, placeholder: 'e.g. Developer' },
                    { name: 'report_types', label: 'Report Types', type: 'multiselect', half: true, placeholder: 'Select report types', options: REPORT_TYPE_OPTIONS },
                    { name: 'is_active', label: 'Active', type: 'checkbox' },
                ]}
                defaultValues={{
                    email: editRecipient?.email || '',
                    name: editRecipient?.extra_data?.name || '',
                    designation: editRecipient?.extra_data?.designation || '',
                    report_types: editRecipient?.report_types || [],
                    is_active: editRecipient?.is_active ?? true,
                }}
                onSubmit={handleEditRecipient}
                error={editRecipientErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setEditRecipient(null), disabled: recipientBusy },
                    { text: 'Save Changes', variant: 'primary', type: 'submit', loading: recipientBusy, loadingText: 'Saving…' },
                ]}
            />

            {/* Delete report recipient confirm */}
            <CommonConfirmModal
                themeColor={false}
                state={delRecipient ? {
                    title: 'Remove Recipient',
                    message: `Remove ${delRecipient?.email || 'this recipient'} from report recipients? They'll stop receiving scheduled reports.`,
                    confirmLabel: recipientBusy ? 'Removing…' : 'Remove',
                    cancelLabel: 'Cancel',
                    danger: true,
                } : null}
                onClose={(ok) => { if (ok) handleDeleteRecipient(); else setDelRecipient(null); }}
            />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Root component
// ═══════════════════════════════════════════════════════════════════════════
function UserManagement() {
    const flags = getUserFlags();
    const isSuperAdmin = flags.role_slug === 'super_admin';
    const canManageOrg = flags.is_org_admin || flags.role_slug === 'mssp' || flags.role_slug === 'tam' || flags.is_platform_admin;

    const organizationId = sessionStorage.getItem('organization_id') || localStorage.getItem('organization_id') || null;

    useEffect(() => {
        const onPlan = (e) => alert(`Upgrade Required: ${e.detail?.message}`);
        const onRbac = (e) => alert(`Permission Denied: ${e.detail?.message}`);
        window.addEventListener('plan_gate_denied', onPlan);
        window.addEventListener('rbac_permission_denied', onRbac);
        return () => {
            window.removeEventListener('plan_gate_denied', onPlan);
            window.removeEventListener('rbac_permission_denied', onRbac);
        };
    }, []);

    if (!isSuperAdmin && !canManageOrg) {
        return (
            <div style={{ ...body({ color: lightTheme.textSecondary }), textAlign: 'center', padding: '48px 0' }}>
                <p>You do not have permission to manage users.</p>
            </div>
        );
    }

    return (
        <div style={S.container}>
            {isSuperAdmin && <PlatformUsersSection />}
            {canManageOrg && (
                <OrgMembersSection organizationId={isSuperAdmin ? null : organizationId} />
            )}
        </div>
    );
}
export default UserManagement;
